// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { createAsyncThunk } from '@reduxjs/toolkit';
import { citecApi } from '@advisor-app/api/engine/citec-api';
import { type PortfolioId } from '@shared/data-access/interfaces/api/engine/citec';
import { buildDataIdeas } from '@advisor-app/features/my-research/utils/build-data-ideas';
import { getAssetsInfo } from '@shared/data-access/transformers/assets/get-assets-info';
import { type SavedPortfolio } from '@shared/data-access/interfaces/redux/my-research-state';
import type { AppDispatch, AppState } from '@advisor-app/store/store';
import { selectIdeas } from './my-research-selectors';
import { portfolioApi } from '@shared/data-access/api/engine/portfolio-api';

export const getSavedPortfolios = createAsyncThunk(
  'myResearch/getSavedPortfolios',
  /* istanbul ignore next */ async (_, { dispatch }) => {
    try {
      const response = await dispatch(
        portfolioApi.endpoints.getAllPortfolios.initiate(
          { detail: true },
          { forceRefetch: true },
        ),
      ).unwrap();

      /* istanbul ignore next */
      const portfolios = response.map(idea => ({
        ...idea,
        holdings: idea.holdings.filter(holding => holding.ticker !== '$CASH'),
      }));

      const tickers_of_ideas = portfolios.flatMap(x =>
        x.holdings.map(holding => holding.ticker),
      );

      const unique_tickers_of_ideas = [...new Set(tickers_of_ideas.flat(1))];

      const data = await getAssetsInfo(
        unique_tickers_of_ideas,
        dispatch as AppDispatch,
      );

      const ideas = buildDataIdeas(portfolios, data);

      return {
        ideas,
        savedPortfolios: portfolios,
      };
    } catch (error) {
      console.trace(error);
      /* istanbul ignore next */ return { ideas: [], savedPortfolios: [] };
    }
  },
);

export const updateSavedPortfolio = createAsyncThunk(
  'myResearch/updateSavedPortfolios',
  /* istanbul ignore next */ async (
    { params, payload }: { params: PortfolioId; payload: SavedPortfolio },
    { getState, dispatch },
  ) => {
    await dispatch(
      portfolioApi.endpoints.updatePortfolio.initiate({
        portfolio_id: params.portfolio_id,
        payload: payload,
      }),
    ).unwrap();

    const portfolios = selectIdeas(getState() as AppState);

    const portfolio = payload as SavedPortfolio;

    const tickers = Array.from(
      new Set(portfolio.holdings.map(holding => holding.ticker)),
    );

    const data = await getAssetsInfo(tickers, dispatch as AppDispatch);

    const ideas = buildDataIdeas(
      portfolios.map(idea =>
        idea.portfolio_id === portfolio.portfolio_id
          ? { ...idea, ...portfolio }
          : { ...idea },
      ),
      data,
    );

    return {
      idea: ideas.find(idea => idea.portfolio_id === portfolio.portfolio_id)!,
      portfolio: portfolio,
    };
  },
);

export const getScreeners = createAsyncThunk(
  'myResearch/getScreeners',
  /* istanbul ignore next */ () => {
    const response = citecApi.universeAll();

    return response;
  },
);
